/**
 * Product Support Guidelines Modal
 *
 * Displays guidelines about what types of questions can be addressed
 * and expected response protocols for the Pharma Medical Team service.
 */

import { X } from 'lucide-react-native';

import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import { PRODUCT_SUPPORT_GUIDELINES } from '@/constants/medicalOnDemand';

type ProductSupportGuidelinesProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function ProductSupportGuidelines({ isOpen, onClose }: ProductSupportGuidelinesProps) {
  const size =
    useBreakpointValue({
      default: 'lg',
      md: 'md',
    }) || 'md';

  return (
    <Modal isOpen={isOpen} size={size} onClose={onClose}>
      <ModalBackdrop />

      <ModalContent className="max-h-[85vh]">
        <ModalHeader>
          <Heading size="h2-md">{PRODUCT_SUPPORT_GUIDELINES.title}</Heading>
          <ModalCloseButton>
            <Icon as={X} size="md" className="text-typography-500" />
          </ModalCloseButton>
        </ModalHeader>

        <Divider className="my-4" />

        <ModalBody className="py-3">
          <VStack space="lg">
            <Text className="text-typography-600">{PRODUCT_SUPPORT_GUIDELINES.intro}</Text>

            {PRODUCT_SUPPORT_GUIDELINES.sections.map((section, sectionIndex) => (
              <VStack key={sectionIndex} space="sm">
                <Heading size="sm">{section.heading}:</Heading>
                <VStack space="xs">
                  {section.items.map((item, itemIndex) => (
                    <Text key={itemIndex} className="text-sm text-typography-700">
                      • {item}
                    </Text>
                  ))}
                </VStack>
              </VStack>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter className="my-3">
          <Button onPress={onClose} className="w-full md:w-auto">
            <ButtonText>Got it</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
